import { useContext, useEffect, useState } from "react";

import { AuthAppContext } from "components/root/auth-app-provider";
import { useKeystone } from "data/ss-data-hooks";
import { UnauthorizedRequestError, isError } from "data/ss-error";
import { handleUnauthorized, logException, trackEvent, useFlags } from "utils";
import {
  type KeystoneOfferEligibility,
  type OfferStatusCode,
} from "utils/keystone-api-mapping";

const ENROLLED_STATUSES: OfferStatusCode[] = ["ENROLLED", "ORDERED"];

export const useFeaturedOffers = () => {
  const keystone = useKeystone();
  const { shingleResealOffer, tingOffer } = useFlags();
  const { userDetails } = useContext(AuthAppContext);
  const [featuredOffers, setFeaturedOffers] = useState<
    KeystoneOfferEligibility[]
  >([]);

  useEffect(() => {
    if (!userDetails?.keystonePolicyId) {
      return;
    }

    const implementedOfferingNames: string[] = [];
    if (tingOffer) {
      implementedOfferingNames.push("ting-offer");
    }
    if (shingleResealOffer) {
      implementedOfferingNames.push("wind-defense");
    }

    (async () => {
      try {
        const res = await keystone.getPolicyOffers(
          userDetails?.keystonePolicyId ?? "",
          true
        );

        if (!isError(res)) {
          const offers: KeystoneOfferEligibility[] = res.filter(
            (offer) =>
              offer.isEligible &&
              !ENROLLED_STATUSES.includes(
                offer?.offerStatusCode || "OFFERED"
              ) &&
              implementedOfferingNames.includes(offer.offeringInternalName)
          );

          setFeaturedOffers(offers);
          trackEvent("Featured Offers Loaded", {
            offers: offers.map((offer) => offer.offeringInternalName),
          });
        } else {
          if (res instanceof UnauthorizedRequestError) {
            handleUnauthorized({
              endpoint: `offers/${userDetails.keystonePolicyId}?featured=true`,
            });
          }
        }
      } catch (err) {
        // swallow the error, we just won't show any featured offers
        logException(err);
      }
    })();
  }, [keystone, shingleResealOffer, tingOffer, userDetails?.keystonePolicyId]);

  return featuredOffers;
};
